import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import API from "../api/axios";
import { useFetch } from "../hooks/useFetch";
import { formatDate, formatTime12 } from "../utils/constants";
import {
  formatCurrency,
  EVENT_SERVICE_LABEL,
  canVerifyEventDistance,
  eventPaymentMeta,
  eventStatusLabel,
  eventStatusTone,
  eventTypeLabel,
  EVENT_REFUND_LABELS,
} from "../utils/eventConstants";
import { useShowToast } from "../store/hooks";
import {
  AlertCircle,
  CalendarCheck,
  Check,
  ChefHat,
  Clock3,
  LocateFixed,
  MapPin,
  RefreshCw,
  RotateCcw,
  UserCheck,
  X,
} from "lucide-react";

// Admin: event booking queue — assign verified cooks + move §15 lifecycle.
const EventBookingAdmin = () => {
  const { data, loading, error, refetch } = useFetch("/event-bookings");
  const { data: refunds, refetch: refetchRefunds } = useFetch("/event-payments/admin/refunds");
  const {
    data: cooks,
    loading: cooksLoading,
    error: cooksError,
    refetch: refetchCooks,
  } = useFetch("/cooks");
  const showToast = useShowToast();
  const [filter, setFilter] = useState("all");
  const [assignFor, setAssignFor] = useState(null);
  const [assignCookIds, setAssignCookIds] = useState([]);
  const [assignKm, setAssignKm] = useState("");
  const [assignAmount, setAssignAmount] = useState("");
  const [busy, setBusy] = useState(null);
  const [refundBusy, setRefundBusy] = useState("");
  const [verifyFor, setVerifyFor] = useState(null);
  const [verifyKm, setVerifyKm] = useState("");
  const verifyLock = useRef(false);

  const bookings = Array.isArray(data) ? data : [];
  const refundRows = Array.isArray(refunds) ? refunds : [];
  const verifiedCooks = (Array.isArray(cooks) ? cooks : []).filter((c) => c.approvalStatus === "approved");

  const visible = bookings.filter((b) => {
    if (filter === "pending") return b.bookingStatus === "pending";
    if (filter === "live") return ["cook_assigned", "confirmed", "in_progress"].includes(b.bookingStatus);
    if (filter === "past") return ["completed", "cancelled"].includes(b.bookingStatus);
    return true;
  });

  const counts = {
    all: bookings.length,
    pending: bookings.filter((b) => b.bookingStatus === "pending").length,
    live: bookings.filter((b) => ["cook_assigned", "confirmed", "in_progress"].includes(b.bookingStatus)).length,
    completed: bookings.filter((b) => b.bookingStatus === "completed").length,
    refunds: refundRows.length,
  };

  const conflictMessage = (err, fallback) => {
    if (err.response?.status === 409) {
      refetch();
      return err.response?.data?.message || "Conflict — another admin just updated this booking. List refreshed.";
    }
    return err.response?.data?.message || fallback;
  };

  const openAssign = (booking) => {
    const assigned = Array.isArray(booking.cookIds) && booking.cookIds.length > 0
      ? booking.cookIds
      : booking.cookId
        ? [booking.cookId]
        : [];
    setAssignFor(booking._id);
    setAssignCookIds(assigned.map((c) => String(c?._id || c)));
    setAssignKm(String(booking.distanceKm ?? ""));
    setAssignAmount(booking.serviceAmount != null ? String(booking.serviceAmount) : "");
  };

  const toggleCook = (cookUserId) => {
    setAssignCookIds((prev) =>
      prev.includes(cookUserId) ? prev.filter((id) => id !== cookUserId) : [...prev, cookUserId]
    );
  };

  const handleAssign = async (booking) => {
    if (busy) return;
    if (assignCookIds.length === 0) {
      showToast("Select at least one verified cook", "error");
      return;
    }
    const rawKm = String(assignKm).trim();
    const dist = Number(rawKm);
    if (!rawKm || !Number.isFinite(dist) || dist < 0 || dist > 100000) {
      showToast("Enter the verified distance in km (0–100000)", "error");
      return;
    }
    let serviceAmount;
    if (String(assignAmount).trim() !== "") {
      serviceAmount = Number(assignAmount);
      if (!Number.isFinite(serviceAmount) || serviceAmount < 0) {
        showToast("Enter a valid service amount", "error");
        return;
      }
    }
    const names = assignCookIds
      .map((id) => verifiedCooks.find((c) => String(c?.user?._id || "") === String(id))?.user?.name)
      .filter(Boolean)
      .join(", ");
    if (
      !window.confirm(
        `Assign ${names || `${assignCookIds.length} cook(s)`} to booking ${booking.bookingId || booking._id.slice(-6).toUpperCase()} with ${dist} km verified?`
      )
    )
      return;
    setBusy(booking._id);
    try {
      await API.post(`/event-bookings/${booking._id}/assign-cook`, {
        cookIds: assignCookIds,
        distanceKm: dist,
        ...(serviceAmount === undefined ? {} : { serviceAmount }),
      });
      showToast("Cook(s) assigned, distance verified and amount finalized — everyone notified!", "success");
      setAssignFor(null);
      setAssignCookIds([]);
      setAssignKm("");
      setAssignAmount("");
      refetch();
    } catch (err) {
      showToast(conflictMessage(err, "Assignment failed"), "error");
    } finally {
      setBusy(null);
    }
  };

  const handleStatus = async (bookingId, status) => {
    if (busy) return;
    if (!window.confirm(`Move booking to ${eventStatusLabel(status)}?`)) return;
    setBusy(bookingId);
    try {
      await API.patch(`/event-bookings/${bookingId}/status`, { status });
      showToast(`Booking marked ${eventStatusLabel(status)}`, "success");
      refetch();
    } catch (err) {
      showToast(conflictMessage(err, "Status update failed"), "error");
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async (bookingId) => {
    if (busy) return;
    if (!window.confirm("Cancel this event booking as admin?")) return;
    setBusy(bookingId);
    try {
      await API.post(`/event-bookings/${bookingId}/cancel`);
      showToast("Booking cancelled", "success");
      refetch();
    } catch (err) {
      showToast(conflictMessage(err, "Cancel failed"), "error");
    } finally {
      setBusy(null);
    }
  };

  const handleVerifyDistance = async (booking) => {
    if (busy || verifyLock.current) return;
    const raw = String(verifyKm).trim();
    const dist = Number(raw);
    if (!raw || !Number.isFinite(dist) || dist < 0 || dist > 100000) {
      showToast("Enter a valid distance in km (0–100000)", "error");
      return;
    }
    if (!canVerifyEventDistance(booking)) {
      showToast("This booking is no longer eligible for distance verification", "error");
      refetch();
      return;
    }
    verifyLock.current = true;
    setBusy(booking._id);
    try {
      await API.patch(`/event-bookings/${booking._id}/verify-distance`, { distanceKm: dist });
      showToast("Distance verified — travel charge recalculated server-side", "success");
      setVerifyFor(null);
      setVerifyKm("");
      refetch();
    } catch (err) {
      showToast(conflictMessage(err, "Verification failed"), "error");
    } finally {
      verifyLock.current = false;
      setBusy(null);
    }
  };

  const handleRefund = async (booking, action) => {
    if (refundBusy) return;
    const amount = formatCurrency(booking.payment?.refundAmount || booking.payment?.paidAmount || 0);
    if (!window.confirm(action === "approve" ? `Approve the full refund of ${amount}?` : "Decline this refund request?")) {
      return;
    }
    const reason =
      action === "approve"
        ? window.prompt("Admin note (optional)") || ""
        : window.prompt("Reason for declining (optional)") || "";
    setRefundBusy(booking._id);
    try {
      await API.post(
        `/event-payments/admin/refunds/${booking._id}/${action}`,
        action === "approve" ? { note: reason } : { reason }
      );
      showToast(action === "approve" ? "Full refund approved" : "Refund declined", "success");
      refetchRefunds();
      refetch();
    } catch (err) {
      showToast(conflictMessage(err, "Refund action failed"), "error");
      refetchRefunds();
    } finally {
      setRefundBusy("");
    }
  };

  const handleReconcileRefund = async (booking) => {
    if (refundBusy) return;
    setRefundBusy(booking._id);
    try {
      const res = await API.post(`/event-payments/admin/refunds/${booking._id}/reconcile`);
      if (res.data?.adopted && res.data?.pending) {
        showToast("Gateway refund is still pending confirmation", "info");
      } else if (res.data?.adopted) {
        showToast("Gateway refund confirmed", "success");
      } else {
        showToast("No gateway refund found — returned to the approval queue", "info");
      }
      refetchRefunds();
      refetch();
    } catch (err) {
      showToast(conflictMessage(err, "Gateway reconciliation failed"), "error");
      refetchRefunds();
    } finally {
      setRefundBusy("");
    }
  };

  const nextActions = (b) => {
    switch (b.bookingStatus) {
      case "cook_assigned":
        return [{ status: "confirmed", label: "Confirm Booking" }];
      case "confirmed":
        return [{ status: "in_progress", label: "Start (In Progress)" }];
      case "in_progress":
        return [{ status: "completed", label: "Mark Completed" }];
      default:
        return [];
    }
  };

  return (
    <div className="event-admin">
      <section className="event-admin-hero">
        <div className="event-admin-hero-copy">
          <span className="event-admin-eyebrow">
            <CalendarCheck size={13} aria-hidden="true" /> Event operations
          </span>
          <h2>Event Bookings</h2>
          <p>Assign verified cooks, verify travel and manage every event from request to completion.</p>
        </div>
        <div className="event-admin-stats" aria-label="Event booking summary">
          <div className="event-admin-stat event-admin-stat--pending">
            <span className="event-admin-stat-icon"><Clock3 size={16} /></span>
            <span className="event-admin-stat-copy"><strong>{counts.pending}</strong><span>Pending</span></span>
          </div>
          <div className="event-admin-stat event-admin-stat--live">
            <span className="event-admin-stat-icon"><ChefHat size={16} /></span>
            <span className="event-admin-stat-copy"><strong>{counts.live}</strong><span>Live events</span></span>
          </div>
          <div className="event-admin-stat event-admin-stat--done">
            <span className="event-admin-stat-icon"><Check size={16} /></span>
            <span className="event-admin-stat-copy"><strong>{counts.completed}</strong><span>Completed</span></span>
          </div>
          <div className="event-admin-stat event-admin-stat--refund">
            <span className="event-admin-stat-icon"><RotateCcw size={16} /></span>
            <span className="event-admin-stat-copy"><strong>{counts.refunds}</strong><span>Refund queue</span></span>
          </div>
        </div>
      </section>

      <div className="event-admin-toolbar">
        <div className="event-filter-row" role="group" aria-label="Filter event bookings">
          {[
            { id: "all", label: "All", count: counts.all },
            { id: "pending", label: "Pending", count: counts.pending },
            { id: "live", label: "Live", count: counts.live },
            { id: "past", label: "Past", count: bookings.length - counts.pending - counts.live },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
              className={`event-filter-pill${filter === f.id ? " is-active" : ""}`}
            >
              {f.label}
              <span className="event-filter-pill-count">{f.count}</span>
            </button>
          ))}
        </div>
        <button type="button" className="event-admin-refresh" onClick={() => refetch()} disabled={loading}>
          <RefreshCw size={14} className={loading ? "is-spinning" : ""} aria-hidden="true" />
          {loading ? "Refreshing" : "Refresh"}
        </button>
      </div>

      {refundRows.length > 0 && (
        <section className="event-refund-panel" aria-labelledby="event-refunds-heading">
          <div className="event-panel-head">
            <h3 id="event-refunds-heading">
              <RotateCcw size={18} /> Event refund approvals ({refundRows.length})
            </h3>
          </div>
          <div className="event-refund-list">
            {refundRows.map((r) => {
              const customer = r.customerId && typeof r.customerId === "object" ? r.customerId : null;
              return (
                <article key={r._id} className="event-refund-card">
                  <div className="event-refund-info">
                    <div className="event-refund-title">
                      <strong>{r.bookingId}</strong>
                      <span className={`event-pay-badge event-pay-badge--${r.payment?.refundStatus === "failed" ? "rose" : "amber"}`}>
                        {EVENT_REFUND_LABELS[r.payment?.refundStatus] || "Awaiting approval"}
                      </span>
                    </div>
                    <p>
                      {customer?.name || "Customer"} · {eventTypeLabel(r)} ·{" "}
                      <strong>{formatCurrency(r.payment?.refundAmount || r.payment?.paidAmount || 0)}</strong>
                    </p>
                    <p className="event-refund-reason">
                      {r.payment?.refundStatus === "failed"
                        ? r.payment?.refundError || "Gateway retry available"
                        : r.payment?.refundStatus === "processing"
                          ? "Refund initiated — check the gateway to confirm whether it completed."
                          : r.payment?.refundReason || "Event cancelled before service start"}
                    </p>
                  </div>
                  <div className="event-refund-actions">
                    {r.payment?.refundStatus === "processing" ? (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={refundBusy === r._id}
                        onClick={() => handleReconcileRefund(r)}
                      >
                        <RefreshCw size={15} /> {refundBusy === r._id ? "Checking..." : "Check gateway"}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          disabled={refundBusy === r._id}
                          onClick={() => handleRefund(r, "approve")}
                        >
                          <Check size={15} /> {refundBusy === r._id ? "Working..." : "Approve full refund"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          disabled={refundBusy === r._id}
                          onClick={() => handleRefund(r, "reject")}
                        >
                          <X size={15} /> Decline
                        </button>
                      </>
                    )}
                    <Link to={`/event-bookings/${r._id}`} className="btn btn-outline btn-sm">
                      View
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {loading ? (
        <div className="loading-spinner-wrapper">
          <div className="spinner"></div>
          <p>Loading event bookings...</p>
        </div>
      ) : error ? (
        <div className="event-state event-state--error" role="alert">
          <span className="event-state-icon">
            <AlertCircle size={24} />
          </span>
          <h3>Couldn't load event bookings</h3>
          <p>{error}</p>
          <button type="button" className="btn btn-outline btn-sm" onClick={refetch}>
            Retry
          </button>
        </div>
      ) : visible.length > 0 ? (
        <div className="event-admin-list">
          {visible.map((b) => {
            const cook = b.cookId && typeof b.cookId === "object" ? b.cookId : null;
            const assignedCooks = (
              Array.isArray(b.cookIds) && b.cookIds.length > 0 ? b.cookIds : [cook]
            ).filter((c) => c && typeof c === "object");
            const cookNames = assignedCooks.map((c) => c.name).filter(Boolean).join(", ");
            const customer = b.customerId && typeof b.customerId === "object" ? b.customerId : null;
            const payMeta = eventPaymentMeta(b);
            const distanceVerified = b.distanceVerified === true;
            const isBusy = busy === b._id;
            return (
              <article key={b._id} className="event-admin-card">
                <header className="event-admin-card-head">
                  <div className="event-admin-heading">
                    <span className="event-admin-ref">{b.bookingId}</span>
                    <h3>{eventTypeLabel(b)}</h3>
                    <p>
                      {customer?.name || "Customer"}
                      {customer?.phone ? ` · ${customer.phone}` : ""} ·{" "}
                      {EVENT_SERVICE_LABEL(b.serviceType)} · {b.guestCount} guests
                    </p>
                  </div>
                  <div className="event-admin-badges">
                    <span className={`event-status-pill event-status-pill--${eventStatusTone(b.bookingStatus)}`}>
                      {eventStatusLabel(b.bookingStatus)}
                    </span>
                    <span className={`event-pay-badge event-pay-badge--${payMeta.tone}`}>{payMeta.label}</span>
                  </div>
                </header>

                <div className="event-admin-facts">
                  <span>
                    <CalendarCheck size={14} /> {formatDate(b.eventDate) || "—"} · {formatTime12(b.startTime) || "—"}
                  </span>
                  <span>
                    <Clock3 size={14} /> {b.duration} hr
                  </span>
                  <span>
                    <MapPin size={14} /> {[b.address, b.area].filter(Boolean).join(", ") || "—"}
                  </span>
                  <span className={distanceVerified ? "" : "is-warning"}>
                    <LocateFixed size={14} /> {b.distanceKm ?? 0} km ·{" "}
                    {distanceVerified ? "verified" : "provisional"}
                  </span>
                  <span>
                    <ChefHat size={14} /> {cookNames || "No cook assigned"}
                    {assignedCooks.length > 1 ? ` (+${assignedCooks.length - 1})` : ""}
                  </span>
                </div>

                <div className="event-admin-cost">
                  <div>
                    <span>Total</span>
                    <strong>{formatCurrency(b.totalAmount)}</strong>
                  </div>
                  <div className="event-admin-cost-parts">
                    <span>Service {formatCurrency(b.serviceAmount)}</span>
                    <span>Travel {Number(b.travelCharge) > 0 ? formatCurrency(b.travelCharge) : "FREE"}</span>
                    {Number(b.extraHourAmount) > 0 && <span>Extra hours {formatCurrency(b.extraHourAmount)}</span>}
                    {Number(b.additionalCookAmount) > 0 && (
                      <span>Additional cook {formatCurrency(b.additionalCookAmount)}</span>
                    )}
                  </div>
                </div>

                {assignFor === b._id ? (
                  <div className="event-admin-inline-panel">
                    {cooksLoading ? (
                      <p className="event-inline-note">
                        <span className="event-spinner" aria-hidden="true" /> Loading verified cooks…
                      </p>
                    ) : cooksError ? (
                      <div className="event-inline-note event-inline-note--error" role="alert">
                        <span>{cooksError}</span>
                        <button type="button" className="btn btn-outline btn-sm" onClick={refetchCooks}>
                          Retry
                        </button>
                      </div>
                    ) : verifiedCooks.length === 0 ? (
                      <p className="event-inline-note">
                        No verified cooks available right now. Assign after a cook is approved.
                      </p>
                    ) : (
                      <>
                        <div className="form-group event-admin-field">
                          <label>Verified cooks</label>
                          <div className="event-admin-cook-options">
                            {verifiedCooks.map((c) => {
                              const id = String(c?.user?._id || "");
                              const selected = assignCookIds.includes(id);
                              return (
                                <label key={c._id} className={`event-admin-cook-option${selected ? " is-selected" : ""}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => toggleCook(id)}
                                  />
                                  <span>
                                    {c?.user?.name} · {c.experienceYears}y · {c.serviceArea || "—"}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        <div className="event-admin-inline-grid">
                          <div className="form-group event-admin-field">
                            <label htmlFor={`assign-km-${b._id}`}>Verified distance (km) *</label>
                            <input
                              id={`assign-km-${b._id}`}
                              type="number"
                              min={0}
                              max={100000}
                              step="0.1"
                              className="form-control"
                              value={assignKm}
                              onChange={(e) => setAssignKm(e.target.value)}
                              placeholder={String(b.distanceKm ?? 0)}
                            />
                          </div>
                          <div className="form-group event-admin-field">
                            <label htmlFor={`assign-amount-${b._id}`}>Service amount (Rs.)</label>
                            <input
                              id={`assign-amount-${b._id}`}
                              type="number"
                              min={0}
                              step="1"
                              className="form-control"
                              value={assignAmount}
                              onChange={(e) => setAssignAmount(e.target.value)}
                              placeholder={String(b.serviceAmount ?? 0)}
                            />
                          </div>
                        </div>
                        <p className="event-inline-note">
                          {assignCookIds.length} cook(s) selected ·{" "}
                          {Math.max(0, assignCookIds.length - 1)} additional cook(s) billed at the current rate.
                          Leave the amount blank to keep the original quote.
                        </p>
                      </>
                    )}
                    <div className="event-admin-inline-actions">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        disabled={isBusy || cooksLoading || Boolean(cooksError) || verifiedCooks.length === 0 || assignCookIds.length === 0}
                        onClick={() => handleAssign(b)}
                      >
                        <Check size={15} aria-hidden="true" /> {isBusy ? "Assigning…" : "Confirm assign"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => {
                          setAssignFor(null);
                          setAssignCookIds([]);
                          setAssignKm("");
                          setAssignAmount("");
                        }}
                      >
                        <X size={15} aria-hidden="true" /> Close
                      </button>
                    </div>
                  </div>
                ) : verifyFor === b._id ? (
                  <div className="event-admin-inline-panel">
                    <div className="form-group event-admin-field">
                      <label htmlFor={`verify-km-${b._id}`}>Verified distance (km)</label>
                      <input
                        id={`verify-km-${b._id}`}
                        type="number"
                        min={0}
                        max={100000}
                        step="0.1"
                        className="form-control"
                        value={verifyKm}
                        onChange={(e) => setVerifyKm(e.target.value)}
                        placeholder={String(b.distanceKm ?? 0)}
                      />
                    </div>
                    <div className="event-admin-inline-actions">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        disabled={isBusy}
                        onClick={() => handleVerifyDistance(b)}
                      >
                        <Check size={15} aria-hidden="true" /> {isBusy ? "Verifying…" : "Confirm distance"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={isBusy}
                        onClick={() => {
                          setVerifyFor(null);
                          setVerifyKm("");
                        }}
                      >
                        <X size={15} aria-hidden="true" /> Close
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="event-admin-actions">
                    {["pending", "cook_assigned"].includes(b.bookingStatus) && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={isBusy}
                        onClick={() => openAssign(b)}
                      >
                        <UserCheck size={15} aria-hidden="true" /> {assignedCooks.length > 0 ? "Reassign cook" : "Assign cook"}
                      </button>
                    )}
                    {canVerifyEventDistance(b) && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={isBusy}
                        onClick={() => {
                          setVerifyFor(b._id);
                          setVerifyKm(String(b.distanceKm ?? 0));
                        }}
                      >
                        <LocateFixed size={15} aria-hidden="true" /> Verify distance ({b.distanceKm ?? 0} km)
                      </button>
                    )}
                    {nextActions(b).map((a) => (
                      <button
                        key={a.status}
                        type="button"
                        className="btn btn-success btn-sm"
                        disabled={isBusy}
                        onClick={() => handleStatus(b._id, a.status)}
                      >
                        <Check size={15} aria-hidden="true" /> {isBusy ? "Working…" : a.label}
                      </button>
                    ))}
                    {!["completed", "cancelled"].includes(b.bookingStatus) && (
                      <button
                        type="button"
                        className="btn btn-danger-outline btn-sm"
                        disabled={isBusy}
                        onClick={() => handleCancel(b._id)}
                      >
                        <X size={15} aria-hidden="true" /> {isBusy ? "Working…" : "Cancel"}
                      </button>
                    )}
                    <Link to={`/event-bookings/${b._id}`} className="btn btn-outline btn-sm">
                      View details
                    </Link>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="event-state">
          <span className="event-state-icon">
            <CalendarCheck size={24} />
          </span>
          <h3>No event bookings here</h3>
          <p>No event bookings match this filter yet.</p>
        </div>
      )}
    </div>
  );
};

export default EventBookingAdmin;
